import { useState, useEffect, useRef } from 'react';
import { Badge } from '../ui/badge';
import { Quote } from 'lucide-react';
import { getFeatureIcon } from './landingConstants';
import { PhoneFrame } from './PhoneFrame';

function AnimatedImage({ mode = 'zoom', direction = 'right', disabled = false, children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(disabled);
  // Honour the user's OS-level reduced-motion preference. Treat it like the
  // explicit `disabled` flag — content snaps into place with no animation.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = (e) => setReduceMotion(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (disabled) { setVisible(true); return; }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [disabled]);

  const animationOff = disabled || reduceMotion;
  let style;
  if (animationOff) {
    style = undefined;
  } else if (mode === 'slide') {
    const offset = direction === 'left' ? '-40px' : '40px';
    style = {
      transform: visible ? 'translateX(0)' : `translateX(${offset})`,
      opacity: visible ? 1 : 0,
      transition: 'transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.9s ease-out',
      willChange: 'transform, opacity',
    };
  } else {
    // 'zoom' — scales up from 0.7 → 1.0 with a soft expo-out curve.
    // Mockup variant #2 (the chosen one).
    style = {
      transform: visible ? 'scale(1)' : 'scale(0.7)',
      opacity: visible ? 1 : 0,
      transformOrigin: 'center center',
      transition: 'transform 0.75s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.75s ease-out',
      willChange: 'transform, opacity',
    };
  }

  return (
    <div ref={ref} style={style} className="flex-1 w-full">
      {children}
    </div>
  );
}

export function FeaturesSection({ content, theme, t }) {
  const hasHeader = content.features_header?.badge || content.features_header?.title || content.features_header?.subtitle;
  const animationDisabled = content.features_header?.animation_disabled === true;
  // Section-wide: when ON, every feature image renders inside the same phone
  // mockup used in the Gallery section. Per-feature `showBrowserFrame: false`
  // still wins as an explicit "no frame" override.
  const usePhoneFrame = content.features_header?.phone_frame === true;
  // Admin-controllable phone dimensions. Both default to sensible values
  // when unset; values are clamped to safe ranges.
  const rawHeight = Number(content.features_header?.phone_height_px);
  const phoneHeight = Number.isFinite(rawHeight) && rawHeight > 0
    ? Math.max(360, Math.min(900, rawHeight))
    : 600;
  const rawWidth = Number(content.features_header?.phone_width_px);
  const phoneWidth = Number.isFinite(rawWidth) && rawWidth > 0
    ? Math.max(200, Math.min(500, rawWidth))
    : 300;
  // CMS-controllable entry animation. Default is 'zoom' (mockup variant #2);
  // 'slide' preserves the legacy horizontal slide-in from left/right.
  const entryAnimation = content.features_header?.phone_entry_animation === 'slide' ? 'slide' : 'zoom';
  return (
    <section key="features" id="features" data-section="features" className="relative pb-20 px-4 sm:px-6 lg:px-8" style={{ paddingTop: hasHeader ? '5rem' : '2rem' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          {content.features_header?.badge && (
            <Badge className="bg-[#007a33] text-white border-[#007a33] mb-4 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px]">
              {content.features_header.badge}
            </Badge>
          )}
          {(content.features_header?.title) && (
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{content.features_header.title}</h2>
          )}
          {(content.features_header?.subtitle) && (
            <p className={`${t.textSecondary} text-lg`}>{content.features_header.subtitle}</p>
          )}
        </div>
        {content.features?.map((feature, index) => {
          const imageOnRight = index % 2 === 0;
          const slideDir = imageOnRight ? 'right' : 'left';
          const quoteBlock = feature.quote ? (
            <div
              className={`${theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-[#007a33]/5'} rounded-xl p-6 ${theme === 'dark' ? '' : t.borderSecondary} border flex gap-4 items-start`}
              data-testid={`feature-quote-${index}`}
            >
              <Quote
                className={`w-10 h-10 shrink-0 -scale-x-100 ${t.accentText} opacity-70`}
                fill="currentColor"
                strokeWidth={0}
                aria-hidden="true"
              />
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} italic text-lg leading-relaxed`}>{feature.quote}</p>
            </div>
          ) : null;
          return (
          <div
            key={feature.id || index}
            className={`flex flex-col ${imageOnRight ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-6 lg:gap-12 mb-24 last:mb-0`}
          >
            <div className="flex-1 w-full">
              <Badge className="bg-[#007a33] text-white border-[#007a33] mb-4 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px]">
                {feature.badge}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">{feature.title}</h2>
              <p className={`${t.textSecondary} text-lg mb-6 leading-relaxed`}>{feature.description}</p>

              {/* Desktop: quote stays in the text column */}
              {quoteBlock && (
                <div className="hidden lg:block">{quoteBlock}</div>
              )}
            </div>

            <AnimatedImage mode={entryAnimation} direction={slideDir} disabled={animationDisabled}>
              {feature.image ? (
                feature.showBrowserFrame === false ? (
                  <img
                    src={feature.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${feature.image}` : feature.image}
                    alt={feature.title}
                    className="w-full h-auto rounded-2xl"
                    loading="lazy"
                    decoding="async"
                  />
                ) : usePhoneFrame ? (
                  <PhoneFrame
                    width={phoneWidth}
                    minHeight={phoneHeight}
                    maxHeight={phoneHeight}
                    showStatusBar={content.features_header?.phone_show_status_bar !== false}
                    statusBarTimeMode={content.features_header?.phone_time_mode || 'apple'}
                    statusBarTime={content.features_header?.phone_status_time}
                    statusBarBg={content.features_header?.phone_status_bar_bg}
                    statusBarIconColor={content.features_header?.phone_status_icon_color}
                    showBrowserBar={content.features_header?.phone_show_browser_bar === true}
                    browserUrl={content.features_header?.phone_browser_url || 'grassrootscoaching.app'}
                    browserBarBg={content.features_header?.phone_browser_bar_bg}
                    screenBg={theme === 'dark' ? 'dark' : 'light'}
                    floorShadow={content.features_header?.phone_floor_shadow === true}
                    floorShadowOffset={content.features_header?.phone_floor_shadow_offset || 0}
                  >
                    <img
                      src={feature.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${feature.image}` : feature.image}
                      alt={feature.title}
                      className="block w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </PhoneFrame>
                ) : (
                <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-2xl ${t.borderSecondary} border overflow-hidden shadow-2xl`}>
                  <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-emerald-700'} px-4 py-2 flex items-center gap-2 ${t.borderSecondary} border-b`}>
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <img
                    src={feature.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${feature.image}` : feature.image}
                    alt={feature.title}
                    className="w-full h-auto"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                )
              ) : (
                <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-2xl ${t.borderSecondary} border overflow-hidden shadow-2xl`}>
                  <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-emerald-700'} px-4 py-2 flex items-center gap-2 ${t.borderSecondary} border-b`}>
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className={`p-8 aspect-video flex items-center justify-center ${theme === 'light' ? 'bg-gray-50' : ''}`}>
                    <div className="text-center">
                      <div className={`w-16 h-16 rounded-full ${theme === 'dark' ? 'bg-[#007a33]/20' : 'bg-[#007a33]/10'} flex items-center justify-center mx-auto mb-4 ${t.accentText}`}>
                        {getFeatureIcon(index)}
                      </div>
                      <p className={t.textSecondary}>{feature.badge}</p>
                    </div>
                  </div>
                </div>
              )}
            </AnimatedImage>

            {/* Mobile: quote sits below the image. Extra top margin accounts
                for the floor shadow under the phone, which extends ~20px
                below the layout box and would otherwise eat the gap. */}
            {quoteBlock && (
              <div className="lg:hidden w-full mt-6">{quoteBlock}</div>
            )}
          </div>
          );
        })}
      </div>
    </section>
  );
}
